/**
 * CustomerSyncDialog
 *
 * Two-step "Sync from QuickBooks" flow:
 *   1. Preview — calls /api/customers/sync-from-quickbooks/preview, shows
 *      a table of QBO customers with the proposed local action (link to
 *      existing, update existing, create new, skip).
 *   2. Confirm — admin un-ticks rows they don't want, hits Apply, we POST
 *      the selected entries to /api/customers/sync-from-quickbooks/confirm.
 *
 * Designed for the typical "first QBO connect" workflow where the admin
 * already has dozens of customers in QBO and just wants them imported.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Loader2, RefreshCw, CheckCircle2, Plus, Link as LinkIcon, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const ACTION_META = {
  update: { label: 'Update existing', color: 'text-blue-300', Icon: RefreshCw },
  link:   { label: 'Link to existing', color: 'text-emerald-300', Icon: LinkIcon },
  create: { label: 'Create new', color: 'text-amber-300', Icon: Plus },
  skip:   { label: 'Skip', color: 'text-slate-500', Icon: AlertCircle },
};

export default function CustomerSyncDialog({ open, onClose, token, onApplied }) {
  const { toast } = useToast();
  const [phase, setPhase] = useState('idle'); // idle | preview | applying | done
  const [previewData, setPreviewData] = useState(null);
  const [decisions, setDecisions] = useState({}); // {qb_customer_id: action}
  const [error, setError] = useState(null);

  const reset = () => {
    setPhase('idle');
    setPreviewData(null);
    setDecisions({});
    setError(null);
  };

  const runPreview = async () => {
    setPhase('preview');
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/customers/sync-from-quickbooks/preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreviewData(data);
      // Default decision = the server's recommended will_action
      const init = {};
      (data.items || []).forEach((it) => { init[it.qb_customer_id] = it.will_action; });
      setDecisions(init);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Preview failed';
      setError(msg);
      setPhase('idle');
    }
  };

  const setRowAction = (qbid, action) => {
    setDecisions((d) => ({ ...d, [qbid]: action }));
  };

  const applyAll = async () => {
    if (!previewData) return;
    setPhase('applying');
    try {
      const entries = (previewData.items || []).map((it) => ({
        qb_customer_id: it.qb_customer_id,
        action: decisions[it.qb_customer_id] || 'skip',
        local_customer_id: it.local_customer_id || null,
      }));
      const { data } = await axios.post(
        `${API}/customers/sync-from-quickbooks/confirm`,
        { entries },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const total = (data.linked || 0) + (data.updated || 0) + (data.created || 0);
      toast({
        title: 'Sync complete',
        description: `${total} customer${total === 1 ? '' : 's'} synced from QuickBooks · ${data.skipped || 0} skipped${data.errors?.length ? ` · ${data.errors.length} errors` : ''}`,
      });
      setPhase('done');
      onApplied?.(data);
    } catch (e) {
      toast({
        title: 'Sync failed',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
      setPhase('preview');
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const items = previewData?.items || [];
  const summary = previewData
    ? `${previewData.count_total} customer${previewData.count_total === 1 ? '' : 's'} in QBO · ${previewData.count_linked} already linked · ${previewData.count_match_name + previewData.count_match_email} to link · ${previewData.count_new} new`
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] flex flex-col" data-testid="customer-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-400" />
            Sync customers from QuickBooks
          </DialogTitle>
          <DialogDescription>
            We&apos;ll pull every parent customer from QuickBooks, auto-match what we can to your existing records, and let you confirm before any changes are made.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: kick off the preview */}
        {!previewData && phase !== 'preview' && (
          <div className="py-8 text-center space-y-4">
            {error && (
              <div className="p-3 rounded-md bg-red-500/10 border border-red-500/40 text-sm text-red-300 text-left" data-testid="customer-sync-error">
                <AlertCircle className="w-4 h-4 inline mr-1.5" />
                {error}
              </div>
            )}
            <Button
              onClick={runPreview}
              className="bg-[#1E88E5] hover:bg-blue-700"
              data-testid="customer-sync-fetch-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Fetch QuickBooks customers
            </Button>
            <p className="text-xs text-slate-500">QBO must be connected under Settings → Integrations.</p>
          </div>
        )}

        {/* Step 1 loading */}
        {phase === 'preview' && !previewData && (
          <div className="py-12 text-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
            Pulling customers from QuickBooks…
          </div>
        )}

        {/* Step 2: review & confirm */}
        {previewData && (
          <>
            <div className="text-xs text-slate-300 mb-2" data-testid="customer-sync-summary">{summary}</div>
            <div className="flex-1 overflow-y-auto border border-[#2D3748] rounded-md" data-testid="customer-sync-list">
              {items.length === 0 ? (
                <div className="p-6 text-center text-slate-400">No customers found in QuickBooks.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#0F1923] sticky top-0">
                    <tr className="text-left text-xs uppercase text-slate-400">
                      <th className="px-3 py-2">QBO Customer</th>
                      <th className="px-3 py-2">Local match</th>
                      <th className="px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const action = decisions[it.qb_customer_id] || it.will_action;
                      const meta = ACTION_META[action] || ACTION_META.skip;
                      const Icon = meta.Icon;
                      return (
                        <tr key={it.qb_customer_id} className="border-t border-[#2D3748]" data-testid={`customer-sync-row-${it.qb_customer_id}`}>
                          <td className="px-3 py-2 align-top">
                            <div className="font-medium text-white">{it.name}</div>
                            {it.email && <div className="text-xs text-slate-400">{it.email}</div>}
                            <div className="text-[10px] text-slate-500 font-mono">QBO #{it.qb_customer_id}</div>
                          </td>
                          <td className="px-3 py-2 align-top text-slate-300">
                            {it.local_name ? (
                              <>
                                <div className="text-emerald-300 text-xs">✓ {it.local_name}</div>
                                <div className="text-[10px] text-slate-500">via {it.match_kind} match</div>
                              </>
                            ) : (
                              <span className="text-slate-500 text-xs italic">no local match</span>
                            )}
                          </td>
                          <td className="px-3 py-2 align-top">
                            <select
                              value={action}
                              onChange={(e) => setRowAction(it.qb_customer_id, e.target.value)}
                              className="bg-[#0F1923] border border-[#2D3748] text-white text-xs rounded px-2 py-1"
                              data-testid={`customer-sync-action-${it.qb_customer_id}`}
                            >
                              {it.match_kind === 'linked' && <option value="update">Update existing</option>}
                              {(it.match_kind === 'name' || it.match_kind === 'email') && <option value="link">Link to existing</option>}
                              {it.match_kind === 'none' && <option value="create">Create new local</option>}
                              <option value="skip">Skip</option>
                            </select>
                            <div className={`text-[10px] mt-1 flex items-center gap-1 ${meta.color}`}>
                              <Icon className="w-3 h-3" />
                              {meta.label}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        <DialogFooter className="mt-3 gap-2">
          <Button variant="ghost" onClick={handleClose} data-testid="customer-sync-close">
            {phase === 'done' ? 'Close' : 'Cancel'}
          </Button>
          {previewData && phase !== 'done' && (
            <Button
              onClick={applyAll}
              disabled={phase === 'applying'}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="customer-sync-apply-btn"
            >
              {phase === 'applying' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Apply changes</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
